import { useState, type ReactNode } from "react";
import type { DeploymentStatus, ApprovalStatus, RollbackClassification } from "../lib/api";
import type { StatusTone } from "./ui";

/**
 * Shared display primitives for the Phase 4 release-management surfaces
 * (review / progress / report / rollback). Presentational only — no data
 * fetching — so they can be unit-tested with mock data.
 *
 * SAFETY POSTURE encoded here:
 *  - No approval is ever a bare one-click action. {@link ConfirmGate} forces the
 *    approver to read explicit terms and tick a required acknowledgement before
 *    the real action becomes available.
 *  - Errors are shown via {@link SafeError}, which only ever renders a
 *    human-readable message — never tokens, headers, stack traces or raw
 *    Discord objects.
 */

// ── Status tone mapping ──────────────────────────────────────────────────────
export function deploymentTone(status: DeploymentStatus): StatusTone {
  switch (status) {
    case "VERIFIED":
    case "ROLLED_BACK":
      return "ok";
    case "PLANNED":
    case "AWAITING_APPROVAL":
    case "APPROVED":
    case "QUEUED":
    case "PREPARING":
    case "APPLYING":
    case "VERIFYING":
    case "ROLLBACK_AVAILABLE":
    case "ROLLBACK_QUEUED":
    case "ROLLING_BACK":
      return "warn";
    case "BLOCKED":
    case "FAILED":
    case "PARTIALLY_APPLIED":
    case "ROLLBACK_FAILED":
    case "STALE":
      return "danger";
    default:
      return "neutral";
  }
}

export function approvalTone(status: ApprovalStatus): StatusTone {
  switch (status) {
    case "APPROVED":
      return "ok";
    case "PENDING":
      return "warn";
    case "REJECTED":
    case "REVOKED":
    case "EXPIRED":
    case "STALE":
      return "danger";
    default:
      return "neutral";
  }
}

export function classificationTone(c: RollbackClassification): StatusTone {
  switch (c) {
    case "SAFE":
      return "ok";
    case "SAFE_WITH_WARNINGS":
    case "MANUAL_REVIEW":
      return "warn";
    case "BLOCKED":
      return "danger";
    default:
      return "neutral";
  }
}

/** Human-readable label for a verification result. */
export function verificationLabel(result: string | null | undefined): string {
  switch (result) {
    case "VERIFIED":
      return "Verified — resulting state matches the approved plan";
    case "VERIFIED_WITH_WARNINGS":
      return "Verified with warnings";
    case "PARTIALLY_APPLIED":
      return "Partially applied — not fully verified";
    case "FAILED_VERIFICATION":
      return "Failed verification";
    case "MANUAL_REVIEW_REQUIRED":
      return "Manual review required";
    default:
      return "Not verified yet";
  }
}

// ── Safe error ───────────────────────────────────────────────────────────────
/**
 * Renders a safe, human-readable error only. Callers must pass a message that
 * already came from the API's typed `error.message` (or a local fallback string)
 * — never a raw exception, header, token or Discord payload.
 */
export function SafeError({ message }: { message: string }): JSX.Element {
  return (
    <div className="phase4-error" role="alert">
      <strong>Something needs your attention</strong>
      <p>{message}</p>
    </div>
  );
}

// ── Progress timeline ────────────────────────────────────────────────────────
export type StepState = "done" | "active" | "pending" | "failed";

export interface TimelineStep {
  key: string;
  label: string;
  state: StepState;
  detail?: ReactNode;
}

export function ProgressTimeline({ steps }: { steps: TimelineStep[] }): JSX.Element {
  return (
    <ol className="timeline" aria-label="Deployment progress">
      {steps.map((s) => (
        <li key={s.key} className={`timeline__step timeline__step--${s.state}`}>
          <span className="timeline__dot" aria-hidden="true" />
          <div className="timeline__body">
            <span className="timeline__label">{s.label}</span>
            <span className="timeline__state">{s.state}</span>
            {s.detail ? <div className="timeline__detail">{s.detail}</div> : null}
          </div>
        </li>
      ))}
    </ol>
  );
}

// ── Confirmation gate ────────────────────────────────────────────────────────
/**
 * An explicit, multi-step confirmation. The primary action button is disabled
 * until the operator ticks the required acknowledgement checkbox — there is
 * deliberately no bare one-click path. Used for both release approval and
 * rollback approval.
 */
export function ConfirmGate({
  terms,
  acknowledgeLabel,
  confirmLabel,
  busy = false,
  disabled = false,
  tone = "danger",
  onConfirm,
}: {
  terms: ReactNode;
  acknowledgeLabel: string;
  confirmLabel: string;
  busy?: boolean;
  disabled?: boolean;
  tone?: "danger" | "primary";
  onConfirm: () => void;
}): JSX.Element {
  const [acknowledged, setAcknowledged] = useState(false);
  const btnClass = tone === "danger" ? "btn btn--danger-confirm" : "btn btn--primary";
  return (
    <div className={`confirm-gate confirm-gate--${tone}`}>
      <div className="confirm-gate__terms">{terms}</div>
      <label className="confirm-gate__ack">
        <input
          type="checkbox"
          checked={acknowledged}
          disabled={disabled || busy}
          onChange={(e) => setAcknowledged(e.target.checked)}
        />
        <span>{acknowledgeLabel}</span>
      </label>
      <button
        type="button"
        className={btnClass}
        disabled={disabled || busy || !acknowledged}
        aria-disabled={disabled || busy || !acknowledged}
        onClick={onConfirm}
      >
        {busy ? "Working…" : confirmLabel}
      </button>
    </div>
  );
}

/** Distinguish "API call succeeded" from "resulting state verified". */
export function ApiVsVerifiedNote(): JSX.Element {
  return (
    <p className="section-note">
      A <strong>successful API call</strong> only means Discord accepted a request. It is <em>not</em> the same as a{" "}
      <strong>verified resulting state</strong> — ODIN re-reads the live server afterwards and only marks a deployment
      VERIFIED when the actual state matches the approved plan.
    </p>
  );
}
